import {
  BaseResponseProps,
  generateFormRules,
  getStandardError,
} from '@qlibs/react-components';
import { Alert, Button, Form, Grid, Input, Select, Switch, message } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { CustomFormItemPer1 } from '../../user/components/CustomFormItem';
import { httpRequest } from '../../../helpers/api';
import {
  ICreateOperationModeDetail,
  OperationModeDetailProperties,
  OperationModeProperties,
  SPEED_OPERATORS,
  isClutchOperationModeDevice,
} from '../types/operationMode.type';
import {
  OperationModeMobileConstantActions,
  OperationModeMobileConstantCancelButton,
  OperationModeMobileConstantForm,
  OperationModeMobileConstantSaveButton,
  OperationModeMobileConstantSwitchField,
  OperationModeMobileField,
} from '../mobile';

function normalizeSpeed(values: {
  speed?: string | null;
  speedOperator?: string | null;
}) {
  const speed =
    values.speed === '' || values.speed === undefined || values.speed === null
      ? null
      : values.speed;

  return {
    speed,
    speedOperator:
      speed === null ||
      values.speedOperator === '' ||
      values.speedOperator === undefined
        ? null
        : values.speedOperator,
  };
}

function normalizeRpmMin(value: unknown): string | null {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  return String(value);
}

interface IProps {
  operationModeId: string;
  operationModeDetailId: string;
  operationModeDetail?: OperationModeDetailProperties;
  ramus2AssetId?: string;
  isRamus2?: boolean;
  onClose: () => void;
}

const OperationModeDetailEdit: React.FC<IProps> = ({
  operationModeId,
  operationModeDetailId,
  operationModeDetail,
  ramus2AssetId,
  isRamus2 = false,
  onClose,
}) => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [isLoadingAction, setIsLoadingAction] = React.useState(false);
  const [data, setData] = React.useState<OperationModeDetailProperties>();
  const [parentDeviceId, setParentDeviceId] = useState<string>('');
  const [form] = Form.useForm();

  const isClutchDevice = useMemo(
    () => isClutchOperationModeDevice(parentDeviceId),
    [parentDeviceId],
  );

  useEffect(() => {
    const fetchParent = async () => {
      try {
        const res = await httpRequest.get<
          BaseResponseProps<OperationModeProperties>
        >('/operation-mode/' + operationModeId);
        const deviceId =
          res.data.payload?.device?.uniqueId ||
          res.data.payload?.deviceId ||
          '';
        setParentDeviceId(deviceId);
      } catch (error) {
        console.error(error);
      }
    };

    if (operationModeId && !isRamus2) {
      fetchParent();
    }
  }, [isRamus2, operationModeId]);

  async function updateOperationMode(values: ICreateOperationModeDetail) {
    try {
      setIsLoadingAction(true);
      if (isRamus2) {
        if (!ramus2AssetId) {
          message.error('Ramus 2.0 asset is not available');
          return;
        }

        await httpRequest.patch(
          `/operation-mode-detail/ramus2/${ramus2AssetId}/speed-config`,
          {
            speedMaxByOperationModeDetailId: {
              [operationModeDetailId]:
                values.speed === '' || values.speed === undefined
                  ? null
                  : values.speed,
            },
          },
        );

        message.success('Ramus 2.0 speed updated');
        onClose();
        return;
      }

      const formData = {
        name: values.name,
        rpmMin: isClutchDevice ? null : normalizeRpmMin(values.rpmMin),
        rpmMax: isClutchDevice ? null : values.rpmMax,
        clutchMax: isClutchDevice ? values.clutchMax : null,
        consumptionRate: values.consumptionRate,
        ...normalizeSpeed(values),
        status: values.status ? 'active' : 'inactive',
      };

      await httpRequest.patch<BaseResponseProps<OperationModeDetailProperties>>(
        '/operation-mode-detail/' + operationModeDetailId,
        formData,
      );

      message.success(`Success update operation mode detail`);
      form.resetFields();
      setData(undefined);
      onClose();
    } catch (error) {
      getStandardError(error, {
        triggerFormValidation: true,
        formRef: form,
        showToast: true,
      });
    } finally {
      setIsLoadingAction(false);
    }
  }

  const handleSubmit = async (values: any) => {
    if (data?.deletedAt) {
      message.warning('This data has been archived and cannot be edited');
    } else {
      updateOperationMode(values);
    }
  };

  async function fetchData(operationModeDetailId: string) {
    try {
      const response = await httpRequest.get<
        BaseResponseProps<OperationModeDetailProperties>
      >('/operation-mode-detail/' + operationModeDetailId);
      if (response.data.payload) {
        const detail = response.data.payload;

        setData(detail);

        const formData = {
          ...detail,
          status: detail.status === 'active' ? true : false,
        };
        form.setFieldsValue(formData);
      }
    } catch (error) {
      message.error(`Failed get operation mode detail data`);
      console.error(error);
    } finally {
      setIsLoadingAction(false);
    }
  }

  useEffect(() => {
    if (isRamus2 && operationModeDetail) {
      setData(operationModeDetail);
      form.setFieldsValue({
        ...operationModeDetail,
        status: operationModeDetail.status === 'active',
      });
      return;
    }

    if (operationModeDetailId) {
      fetchData(operationModeDetailId);
    }
  }, [form, isRamus2, operationModeDetail, operationModeDetailId]);

  const rpmMinRules = isRamus2
    ? undefined
    : [
        {
          pattern: /^(?:\d+(?:\.\d+)?)?$/,
          message: 'RPM Min must be a non-negative number.',
        },
        ({ getFieldValue }: { getFieldValue: (name: string) => unknown }) => ({
          validator(_: unknown, value: unknown) {
            if (value === undefined || value === null || value === '') {
              return Promise.resolve();
            }

            const maxValue = getFieldValue('rpmMax');
            if (maxValue === undefined || maxValue === null || maxValue === '') {
              return Promise.resolve();
            }

            const min = Number(value);
            const max = Number(maxValue);
            if (Number.isFinite(min) && Number.isFinite(max) && min > max) {
              return Promise.reject(
                new Error('RPM Min cannot be greater than RPM Max.'),
              );
            }

            return Promise.resolve();
          },
        }),
      ];

  const rpmMinField = isMobile ? (
    <OperationModeMobileField label='RPM Min' name='rpmMin' rules={rpmMinRules}>
      <Input
        disabled={isRamus2}
        placeholder='Optional rpm min'
        type='number'
      />
    </OperationModeMobileField>
  ) : (
    <CustomFormItemPer1 label='RPM Min' name='rpmMin' rules={rpmMinRules}>
      <Input
        disabled={isRamus2}
        placeholder='Optional rpm min'
        type='number'
      />
    </CustomFormItemPer1>
  );

  const thresholdField =
    isRamus2 || !isClutchDevice ? (
      <>
        {rpmMinField}
        {isMobile ? (
        <OperationModeMobileField
          label='RPM Max'
          name='rpmMax'
          rules={
            isRamus2
              ? undefined
              : [
                  {
                    pattern: /^(?:\d+(?:\.\d+)?)?$/,
                    message: 'RPM Max must be a non-negative number.',
                  },
                ]
          }
        >
          <Input
            disabled={isRamus2}
            placeholder='Input rpm max'
            type='number'
          />
        </OperationModeMobileField>
      ) : (
        <CustomFormItemPer1
          label='RPM Max'
          name='rpmMax'
          rules={
            isRamus2
              ? undefined
              : [
                  {
                    pattern: /^(?:\d+(?:\.\d+)?)?$/,
                    message: 'RPM Max must be a non-negative number.',
                  },
                ]
          }
        >
          <Input
            disabled={isRamus2}
            placeholder='Input rpm max'
            type='number'
          />
        </CustomFormItemPer1>
        )}
      </>
    ) : isMobile ? (
      <OperationModeMobileField
        label='Clutch Max'
        name='clutchMax'
        rules={generateFormRules('Clutch Max', ['numeric', 'required'])}
      >
        <Input placeholder='Input clutch max' type='number' />
      </OperationModeMobileField>
    ) : (
      <CustomFormItemPer1
        label='Clutch Max'
        name='clutchMax'
        rules={generateFormRules('Clutch Max', ['numeric', 'required'])}
      >
        <Input placeholder='Input clutch max' type='number' />
      </CustomFormItemPer1>
    );

  const speedRules = [
    {
      pattern: /^(?:\d+(?:\.\d+)?)?$/,
      message: 'Speed must be a non-negative number.',
    },
  ];
  const speedOperatorOptions = SPEED_OPERATORS.map((operator) => ({
    value: operator,
    label: operator,
  }));
  const speedOperatorField = isRamus2 ? null : isMobile ? (
    <OperationModeMobileField label='Speed Operator' name='speedOperator'>
      <Select
        allowClear
        options={speedOperatorOptions}
        placeholder='Select operator'
      />
    </OperationModeMobileField>
  ) : (
    <CustomFormItemPer1 label='Speed Operator' name='speedOperator'>
      <Select
        allowClear
        options={speedOperatorOptions}
        placeholder='Select operator'
      />
    </CustomFormItemPer1>
  );
  const speedFields = isMobile ? (
    <>
      <OperationModeMobileField label='Speed' name='speed' rules={speedRules}>
        <Input placeholder='Optional speed' suffix='knot' type='number' />
      </OperationModeMobileField>
      {speedOperatorField}
    </>
  ) : (
    <>
      <CustomFormItemPer1 label='Speed' name='speed' rules={speedRules}>
        <Input placeholder='Optional speed' suffix='knot' type='number' />
      </CustomFormItemPer1>
      {speedOperatorField}
    </>
  );

  if (isMobile) {
    return (
      <OperationModeMobileConstantForm>
        {data?.deletedAt ? (
          <Alert
            className='mb-5'
            type='warning'
            message='This data has been archived and cannot be edited'
          />
        ) : (
          false
        )}
        {isRamus2 && (
          <Alert
            className='mb-5'
            type='info'
            message='Name, RPM Max, Consumption Rate, and Status are managed by Ramus 2.0. Only Speed is configurable here.'
          />
        )}
        <Form
          form={form}
          name='profileForm'
          layout='vertical'
          onFinish={handleSubmit}
          autoComplete='off'
        >
          <OperationModeMobileField
            label='Name'
            name='name'
            rules={generateFormRules('Name', ['required'])}
          >
            <Input disabled={isRamus2} placeholder='Input name' />
          </OperationModeMobileField>
          {thresholdField}
          <OperationModeMobileField
            label='Consumption Rate'
            name='consumptionRate'
            rules={generateFormRules('Consumption Rate', ['required'])}
          >
            <Input
              disabled={isRamus2}
              placeholder='Input consumption rate'
              suffix='L/m'
            />
          </OperationModeMobileField>
          {speedFields}

          <OperationModeMobileConstantSwitchField
            label='Status'
            name='status'
            valuePropName='checked'
            initialValue={true}
          >
            <Switch
              defaultChecked
              disabled={isRamus2}
              className='custom-switch'
            />
          </OperationModeMobileConstantSwitchField>

          <OperationModeMobileConstantActions>
            <OperationModeMobileConstantCancelButton onClick={onClose}>
              Cancel
            </OperationModeMobileConstantCancelButton>
            <OperationModeMobileConstantSaveButton
              loading={isLoadingAction}
              type='primary'
              htmlType='submit'
            >
              Save
            </OperationModeMobileConstantSaveButton>
          </OperationModeMobileConstantActions>
        </Form>
      </OperationModeMobileConstantForm>
    );
  }

  return (
    <React.Fragment>
      {data?.deletedAt ? (
        <Alert
          className='mb-5'
          type='warning'
          message='This data has been archived and cannot be edited'
        />
      ) : (
        false
      )}
      {isRamus2 && (
        <Alert
          className='mb-5'
          type='info'
          message='Name, RPM Max, Consumption Rate, and Status are managed by Ramus 2.0. Only Speed is configurable here.'
        />
      )}
      <Form
        form={form}
        name='profileForm'
        layout='vertical'
        onFinish={handleSubmit}
        autoComplete='off'
      >
        <CustomFormItemPer1
          label='Name'
          name='name'
          rules={generateFormRules('Name', ['required'])}
        >
          <Input disabled={isRamus2} placeholder='Input name' />
        </CustomFormItemPer1>
        {thresholdField}
        <CustomFormItemPer1
          label='Consumption Rate'
          name='consumptionRate'
          rules={generateFormRules('Consumption Rate', ['required'])}
        >
          <Input
            disabled={isRamus2}
            placeholder='Input consumption rate'
            suffix='L/m'
          />
        </CustomFormItemPer1>
        {speedFields}

        <CustomFormItemPer1
          label='Status'
          name='status'
          valuePropName='checked'
          initialValue={true}
        >
          <Switch
            defaultChecked
            disabled={isRamus2}
            className='custom-switch'
          />
        </CustomFormItemPer1>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
          }}
        >
          <Button onClick={onClose}>Cancel</Button>
          <Button loading={isLoadingAction} type='primary' htmlType='submit'>
            Save
          </Button>
        </div>
      </Form>
    </React.Fragment>
  );
};

export default OperationModeDetailEdit;
